import { Component, OnInit } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ActivatedRoute, Router } from '@angular/router';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { api_routes, db_tables, routes } from '@app/consts';
import { defer, forkJoin } from 'rxjs';
import { LettersService } from '../../services/letters.service';
import { LetterTemplate } from '../../models/letters.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { RowActionButtonInput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';

@Component({
  selector: 'app-letter-templates',
  templateUrl: './letter-templates.component.html',
  styleUrls: ['./letter-templates.component.scss']
})
export class LetterTemplatesComponent implements OnInit {
  public columns: any[] = [
    { field: "name", title: "Name", tableId: "tfi_LtrHeaderName" },
    { field: "description", title: "description", tableId: "tfi_LtrHeaderDescription", type: "longText", longTextLimit: 200 },
    { field: "status", subField: "name", tableId: "tfi_LtrHeaderStatus", title: "Status", type: 'chip', chipValue: ['Active', 'Archived', 'Draft'] },
  ];

  filterCategories: any[] = [
    { field: "name", title: "Name", tableId: "tfi_LtrHeaderName", type: "String", dataType: 'String' },
    { field: "description", title: "description", tableId: "tfi_LtrHeaderDescription", type: "String", dataType: 'String' },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name"},
    { field: "description", sortValue: "description"},
    { field: "status", sortValue: "status.name"},
  ];

  filterToggleDetails: {
    text: string,
    checked: boolean
  } = {
    text: 'ShowArchived',
    checked: false
  }

  gridActionButtons: RowActionButtonInput[] = [
    {
      id: 'copy',
      name: this.translate.instant('Copy'),
      icon: 'content_copy'
    }
  ];

  public bindingType: String = "array";
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 10;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formId: string = 'frm_k6zSoWtEfWyMwg'
  dialogRef: any;
  formValid: any;
  searchValue: string;
  searchFilterString: string;
  getGoalsTypesRequest: any;
  sortString: string;
  filterString: string;
  error: boolean;
  getLetterTemplatesRequest: any;
  accessAndHistoryURL: string = `${api_routes.LETTERS}/${api_routes.TEMPLATES}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private overlayService: OverlayService,
    private router: Router,
    public lettersService: LettersService
  ) { }

  ngOnInit(): void {
    this.getLetterTemplates();
  }

  getLetterTemplates() {
    let filter;
    if(this.searchFilterString) {
      filter = this.searchFilterString;
    }
    if(this.filterString) {
      if(filter){
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }
    if(this.filterToggleDetails.checked === false) {
      if(filter === undefined) {
        filter = `(Status.Id != "Archived")`;
      }
      else {
        filter = filter + `AND (Status.Id != "Archived")`;
      }
    }

    this.isLoading = true;

    this.getLetterTemplatesRequest = this.lettersService.getLetterTemplates(this.pageSize, this.skip, this.sortString, filter)
    .subscribe(
      {
        next: (v) => {
          this.gridDataResult = {
            data: v.data,
            total: v.total,
          }
        },
        error: (e) => this.error = true,
        complete: () => this.isLoading = false
      }
    );
  }

  openEditor(letterTemplate: LetterTemplate) {
    if(letterTemplate) {
      this.router.navigate([`${routes.LETTERS}/${routes.TEMPLATES}${routes.EDITOR}/${letterTemplate?.id}`]);
    }
    else{
      this.router.navigate([`${routes.LETTERS}/${routes.TEMPLATES}${routes.EDITOR}`]);
    }
  }

  deleteItems(ids: string[]) {
    const observables = ids.map(selectedItem => defer(() => this.lettersService.deleteLetterTemplate(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .subscribe(
      {
        next: (v) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
          this.clearSelectedItems = !this.clearSelectedItems;
          this.getLetterTemplates();
        },
        error: (e) => this.error = true,
        complete: () => this.overlayService.hide()
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getLetterTemplates();
  }

  search(newSearchString: string){
    this.searchValue = newSearchString;

    let variants = this.searchValue.split(' ').filter(value => value);
    this.searchFilterString = '';
    variants.map(variant => {
        if (variant) {
            if (this.searchFilterString.length > 2) {
                this.searchFilterString += ' AND ';
            }
            this.searchFilterString += `(name like "${variant}")`;
        }
    });

    this.getLetterTemplatesRequest.unsubscribe();
    this.getLetterTemplates();
  }

  sortChange(sortString: string){
    this.sortString = sortString;
    this.getLetterTemplates();
  }

  filterCallback(filterString: string) {
    this.filterString = filterString;
    this.skip = 0;;
    this.getLetterTemplates();
  }

  updateFilterToggleDetails(toggleChange: MatSlideToggleChange) {
    this.filterToggleDetails.checked = toggleChange.checked;
    this.getLetterTemplates();
  }

  copy(event) {
    let lettertemplate = event.rowData;
    this.overlayService.show();

    this.lettersService.copyLetterTemplate(lettertemplate.id)
    .subscribe(
      {
        next: (v) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CopiedSuccessfully')}`, 'clear', 'success');
          this.router.navigate([`${routes.LETTERS}/${routes.TEMPLATES}${routes.EDITOR}/${v.id}`]);
        },
        error: (e) => this.overlayService.hide(),
        complete: () => this.overlayService.hide()
      }
    );
  }
}

